import React, { useState, useEffect } from 'react';
import { Card, Select, Empty, Spin, message } from 'antd';
import { BgColorsOutlined, CopyOutlined } from '@ant-design/icons';
import apiClient from '../../../api/client';
import NodeLabel from './NodeLabel';
import { useNodeTitle } from './useNodeTitle';
import CustomResizer from './CustomResizer';
import { NODE_BG } from './nodeStyles';

const ColorPaletteNode = ({ data, id, selected }) => {
    const [loading, setLoading] = useState(false);
    const [palettes, setPalettes] = useState([]);
    const [selectedPalette, setSelectedPalette] = useState(null);
    const onTitleChange = useNodeTitle(id, data);

    // Fetch palettes for the current canvas
    useEffect(() => {
        const fetchPalettes = async () => {
            if (!data.canvasId) return;
            setLoading(true);
            try {
                const response = await apiClient.get('/color-palettes', {
                    params: { canvasId: data.canvasId }
                });
                setPalettes(response.data);

                // Restore selection if saved in data
                if (data.selectedPaletteId) {
                    const saved = response.data.find(p => p.id === data.selectedPaletteId);
                    if (saved) setSelectedPalette(saved);
                }
            } catch (error) {
                console.error('Failed to fetch palettes:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchPalettes();
    }, [data.canvasId, data.selectedPaletteId]);

    const handlePaletteChange = (value) => {
        const palette = palettes.find(p => p.id === value);
        setSelectedPalette(palette);
        // Persist to node data
        if (data.onSave) {
            data.onSave(id, { selectedPaletteId: value });
        }
    };

    const handleCopyColor = (color) => {
        navigator.clipboard.writeText(color);
        message.success(`Copied ${color}`);
    };

    // Helper for text contrast
    const getContrastColor = (hex) => {
        const r = parseInt(hex.substr(1, 2), 16);
        const g = parseInt(hex.substr(3, 2), 16);
        const b = parseInt(hex.substr(5, 2), 16);
        const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
        return (yiq >= 128) ? '#000' : '#fff';
    };

    return (
        <div style={{ position: 'relative', height: '100%', width: '100%' }}>
            <CustomResizer
                isVisible={selected}
                minWidth={300}
                minHeight={200}
                onResizeEnd={(e, params) => data.onResize && data.onResize(id, params)}
            />

            <Card
                title={
                    <NodeLabel
                        label={data.label || 'Color Palette'}
                        title={data.nodeTitle || 'Color Palette'}
                        iconType="bg-colors"
                        color="#eb2f96"
                        onTitleChange={onTitleChange}
                    />
                }
                style={{ width: '100%', height: '100%', backgroundColor: NODE_BG, border: '1px solid #30363d', display: 'flex', flexDirection: 'column' }}
                bodyStyle={{ padding: 12, flex: 1, display: 'flex', flexDirection: 'column', gap: 12, overflow: 'hidden', background: NODE_BG }}
            >
                <div className="nodrag">
                    <Select
                        placeholder="Select a palette"
                        style={{ width: '100%' }}
                        loading={loading}
                        value={selectedPalette?.id}
                        onChange={handlePaletteChange}
                        options={palettes.map(p => ({ label: p.name, value: p.id }))}
                        dropdownStyle={{ backgroundColor: NODE_BG, border: '1px solid #30363d' }}
                    />
                </div>

                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 12, minHeight: 0 }}>
                    {selectedPalette ? (
                        <>
                            {/* Horizontal Bar Layout (Weighted) */}
                            <div style={{
                                flex: 2,
                                display: 'flex',
                                flexDirection: 'row',
                                borderRadius: 6,
                                overflow: 'hidden',
                                border: '1px solid #30363d',
                                minHeight: 40
                            }}>
                                {selectedPalette.colors.map((item, i) => {
                                    // Handle both string and object formats
                                    const color = typeof item === 'string' ? item : item.color;
                                    const weight = typeof item === 'string' ? null : item.weight;

                                    // If weight exists, use it for flex-grow
                                    const flexValue = weight ? weight * 100 : 1;

                                    return (
                                        <div
                                            key={i}
                                            style={{
                                                flex: flexValue,
                                                minWidth: 20,
                                                backgroundColor: color,
                                                position: 'relative',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: 9,
                                                fontWeight: 600,
                                                color: getContrastColor(color),
                                                letterSpacing: 0.5,
                                                lineHeight: 1.1,
                                                padding: 2,
                                                overflow: 'hidden'
                                            }}
                                            title={`${color} ${weight ? `(${Math.round(weight * 100)}%)` : ''}`}
                                        >
                                            <div style={{ transform: 'rotate(-90deg)', whiteSpace: 'nowrap' }}>
                                                <span>{color.toUpperCase()}</span>
                                                {weight && <span style={{ opacity: 0.8, marginLeft: 4 }}>{Math.round(weight * 100)}%</span>}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Clickable Squares Row */}
                            <div className="nodrag" style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 8,
                                justifyContent: 'flex-start',
                                maxHeight: 100,
                                overflowY: 'auto'
                            }}>
                                {selectedPalette.colors.map((item, i) => {
                                    const color = typeof item === 'string' ? item : item.color;
                                    return (
                                        <div
                                            key={i}
                                            onClick={() => handleCopyColor(color)}
                                            style={{
                                                width: 32,
                                                height: 32,
                                                backgroundColor: color,
                                                borderRadius: 6,
                                                cursor: 'pointer',
                                                border: '1px solid #30363d',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                transition: 'transform 0.1s',
                                            }}
                                            title={`Click to copy ${color}`}
                                            onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
                                            onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                                        >
                                            {/* Optional icon on hover? Keeping it clean for now, just the color */}
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    ) : (
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Empty description={<span style={{ color: '#666' }}>No palette selected</span>} image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        </div>
                    )}
                </div>
            </Card>
        </div>
    );
};

export default ColorPaletteNode;
